import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { sampleCaptionData } from '../data/sampleCaptions';
import { editorService } from '../../api/editorService';
import { translate } from '../../i18n';

const CaptionContext = createContext();
const STORAGE_KEY = 'arabcap_editor_data';

// Generate unique ID for translation words
const generateTranslationId = (prefix = 'tw') => `${prefix}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useCaptions = () => {
  const context = useContext(CaptionContext);
  if (!context) {
    throw new Error('useCaptions must be used within CaptionProvider');
  }
  return context;
};

// Generate unique ID
const generateId = (prefix) => `${prefix}${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Common punctuation characters for detection
const PUNCTUATION_CHARS = /^[.,!?;:'"()\[\]{}<>\/\\@#$%^&*+=|~`\-—–…،؛؟٪]+$/;

// Initialize editable translation data from server translation
// Creates 1 word per skeleton for English (LTR) translation
const initializeTranslationFromServer = (serverTranslation) => {
  if (!serverTranslation || !serverTranslation.words || serverTranslation.words.length === 0) {
    return null;
  }
  
  // Process words - detect punctuation properly
  const words = serverTranslation.words.map((word, index) => {
    const text = word.text || '';
    const isPunctuation = PUNCTUATION_CHARS.test(text.trim());
    
    return {
      ...word,
      id: word.id || generateTranslationId(`tw${index}_`),
      type: isPunctuation ? 'punctuation' : (word.type || 'word'),
      // For punctuation, attach to previous word
      attaches_to: isPunctuation ? 'previous' : undefined,
    };
  });
  
  // Build skeleton - 1 word per skeleton for English translation
  // This makes each word its own "line" in Studio view
  const skeleton = [];
  
  words.forEach((word) => {
    // Skip standalone punctuation in skeleton creation (they'll attach to previous)
    if (word.type === 'punctuation' && word.attaches_to === 'previous') {
      // Add punctuation to the previous skeleton if exists
      if (skeleton.length > 0) {
        skeleton[skeleton.length - 1].words.push(word.id);
        if (word.end_time) {
          skeleton[skeleton.length - 1].end_time = word.end_time;
        }
      }
      return;
    }
    
    // Create a new skeleton for each non-punctuation word
    skeleton.push({
      id: generateTranslationId('ts'),
      words: [word.id],
      start_time: word.start_time || 0,
      end_time: word.end_time || 0,
      size: 'medium',
      positionY: null,
      emphasize: false,
      disabled: false
    });
  });
  
  return {
    skeleton,
    words,
    source_language: 'ar',
    target_language: 'en'
  };
};

// Punctuation mapping for Arabic <-> Latin conversion
const ARABIC_TO_LATIN_PUNCT = {
  '،': ',',  // Arabic comma to Latin comma
  '؛': ';',  // Arabic semicolon to Latin semicolon
  '؟': '?',  // Arabic question mark to Latin question mark
  '٪': '%',  // Arabic percent to Latin percent
  '٫': '.',  // Arabic decimal separator to Latin period
  '٬': ',',  // Arabic thousands separator to Latin comma
  '۔': '.',  // Arabic full stop to Latin period
};

const LATIN_TO_ARABIC_PUNCT = {
  ',': '،',  // Latin comma to Arabic comma
  ';': '؛',  // Latin semicolon to Arabic semicolon
  '?': '؟',  // Latin question mark to Arabic question mark
  '.': '.',  // Period stays the same (common to both scripts)
};

// Convert punctuation text based on script direction
const convertPunctuation = (text, toScript) => {
  if (!text) return text;
  const mapping = toScript === 'latin' ? ARABIC_TO_LATIN_PUNCT : LATIN_TO_ARABIC_PUNCT;
  let result = text;
  for (const [from, to] of Object.entries(mapping)) {
    result = result.split(from).join(to);
  }
  return result;
};

// Calculate skeleton bounds from word timestamps
const calculateSkeletonBounds = (wordIds, allWords, fallbackStart = 0, fallbackEnd = 1) => {
  const words = wordIds
    .map(id => allWords.find(w => w.id === id))
    .filter(w => w && w.start_time !== undefined && w.end_time !== undefined);

  if (words.length === 0) {
    return { start_time: fallbackStart, end_time: fallbackEnd };
  }

  return {
    start_time: Math.min(...words.map(w => w.start_time)),
    end_time: Math.max(...words.map(w => w.end_time))
  };
};

export const CaptionProvider = ({ children }) => {
  const [captionData, setCaptionData] = useState(sampleCaptionData);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoSrc, setVideoSrc] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);
  const [serverSyncEnabled, setServerSyncEnabled] = useState(false);
  const [displayMode, setDisplayModeInternal] = useState('original'); // 'original' or 'arabizi'
  const [phraseDisplayMode, setPhraseDisplayMode] = useState('default'); // 'default', 'code_switched', 'transliterated', 'translated'
  const [previousPhraseMode, setPreviousPhraseMode] = useState('default'); // Store mode before arabizi switch
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Auto-scroll toggle
  const [editorReady, setEditorReady] = useState(false);
  
  // Ref to always access latest captionData (avoids stale closure issues)
  const captionDataRef = useRef(captionData);
  
  // Translation state
  const [translationData, setTranslationData] = useState(null); // User-editable translation { skeleton, words }
  const [serverTranslation, setServerTranslation] = useState(null); // Original translation from server
  const [translationRequested, setTranslationRequested] = useState(false);
  const [isTranslationView, setIsTranslationView] = useState(false); // Toggle between original and translated view
  const [translationStatus, setTranslationStatus] = useState('none'); // 'none', 'pending', 'completed', 'error'
  
  // Settings (only functional settings, no visual theme settings)
  const [themeSettings, setThemeSettings] = useState({
    // Text segmentation settings
    wordsPerLine: 4, // Number of words per line before auto-break
    enableAutoLineBreak: false, // Enable automatic line breaks
    chunkMode: 'words', // 'words' or 'characters' - how to measure chunk size
    wordsPerChunk: 3, // Max words per skeleton/chunk (when chunkMode is 'words')
    charsPerChunk: 50, // Max characters per skeleton/chunk (when chunkMode is 'characters')
    separateScriptChunks: false, // Separate Latin phrase words from Arabic words in chunks
    silenceAwareChunks: false, // Split chunks when silence tokens appear
    punctuationAwareChunks: false, // Split chunks when punctuation appears
    uppercaseEverything: true, // Uppercase all output text for non-Arabic languages (default ON)
    // Font settings
    proseFontFamily: 'Noto Naskh Arabic', // Default Arabic font
    latinFontFamily: 'Inter', // Default Latin font
    proseFontSize: 1.25, // Font size in rem (default 1.25rem = 20px)
    // Punctuation settings
    showPunctuation: true, // Show or hide punctuation marks
    // Speaker settings (Advanced)
    splitBySpeaker: false, // Split chunks at speaker changes
    speakerColorEnabled: false, // Alternate text color on speaker change
    speakerDashEnabled: false // Prefix '-' when speaker changes
  });
  
  // Undo/Redo history - using refs to avoid stale closures
  const historyRef = useRef([sampleCaptionData]);
  const historyIndexRef = useRef(0);
  const [, forceUpdate] = useState({}); // For triggering re-renders when needed
  const maxHistorySize = 50; // Limit history to prevent memory issues
  
  // Keep captionDataRef in sync with captionData state
  useEffect(() => {
    captionDataRef.current = captionData;
  }, [captionData]);
  
  // Toast notification state
  const [toast, setToast] = useState(null);
  const toastTimeoutRef = useRef(null);
  
  // State to track recently changed words for animation feedback
  const [changedWordIds, setChangedWordIds] = useState(new Set());
  
  // State to track newly inserted words (until they are edited)
  const [newWordIds, setNewWordIds] = useState(new Set());
  
  // Video element ref for timeline editor access
  const videoRef = useRef(null);
  
  // Ref to store seek callback from video component
  const seekCallbackRef = useRef(null);
  
  // Show toast notification
  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message: translate(message), type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, 1500);
  }, []);

  // Helper to mark words as changed (triggers animation feedback)
  const markWordsAsChanged = useCallback((wordIds) => {
    const ids = Array.isArray(wordIds) ? wordIds : [wordIds];
    setChangedWordIds(prev => new Set([...prev, ...ids]));
    
    // Auto-clear after animation duration
    setTimeout(() => {
      setChangedWordIds(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }, 800);
  }, []);

  // Helper to mark words as newly inserted (persists until edited)
  const markWordsAsNew = useCallback((wordIds) => {
    const ids = Array.isArray(wordIds) ? wordIds : [wordIds];
    setNewWordIds(prev => new Set([...prev, ...ids]));
  }, []);

  // Helper to clear "new" status from words (called when word is edited)
  const clearNewWordStatus = useCallback((wordIds) => {
    const ids = Array.isArray(wordIds) ? wordIds : [wordIds];
    setNewWordIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  // Register seek callback from video component
  const registerSeekCallback = useCallback((callback) => {
    seekCallbackRef.current = callback;
  }, []);

  // Register video element reference for timeline editor
  const registerVideoRef = useCallback((ref) => {
    videoRef.current = ref;
  }, []);

  // Seek to a specific time
  const seekToTime = useCallback((time) => {
    if (seekCallbackRef.current) {
      seekCallbackRef.current(time);
    }
    setCurrentTime(time);
  }, []);

  // Add state to history (defined first since other functions depend on it)
  const addToHistory = useCallback((newData) => {
    if (!newData || !newData.skeleton || !newData.words) {
      console.warn('Invalid data passed to addToHistory', newData);
      return;
    }
    
    // Remove any future history if we're not at the end
    const currentIndex = historyIndexRef.current;
    const newHistory = historyRef.current.slice(0, currentIndex + 1);
    
    // Add new state
    newHistory.push(JSON.parse(JSON.stringify(newData))); // Deep clone to prevent mutations
    
    // Limit history size
    if (newHistory.length > maxHistorySize) {
      newHistory.shift();
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
    } else {
      historyRef.current = newHistory;
      historyIndexRef.current = newHistory.length - 1;
    }
  }, []);

  const updateCaptionData = useCallback((newData, skipHistory = false) => {
    // Merge with existing data to preserve fields like 'phrases' when not explicitly set
    setCaptionData(prevData => {
      const merged = {
        ...prevData,
        ...newData,
        // Ensure phrases is preserved if not explicitly provided
        phrases: newData.phrases !== undefined ? newData.phrases : (prevData.phrases || [])
      };
      
      // Keep ref in sync immediately to avoid stale reads
      captionDataRef.current = merged;
      
      // Add to history unless it's a data load or undo/redo operation
      if (!skipHistory) {
        // Check if the new state is actually different from the last history state
        const lastState = historyRef.current[historyIndexRef.current];
        const mergedStr = JSON.stringify(merged);
        const lastStr = lastState ? JSON.stringify(lastState) : '';
        
        // Only add to history if actually different
        if (mergedStr !== lastStr) {
          addToHistory(merged);
          
          if (!serverSyncEnabled) {
            // Save to localStorage on edits (debounced)
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify({
                jsonData: merged,
                timestamp: Date.now()
              }));
            } catch (err) {
              console.error('Failed to save to localStorage:', err);
            }
          }
        }
      } else {
        // If skipping history (initial load), reset history to this state
        historyRef.current = [JSON.parse(JSON.stringify(merged))];
        historyIndexRef.current = 0;
      }
      
      return merged;
    });
  }, [addToHistory, serverSyncEnabled]);

  const loadFromServer = useCallback(async (jobId) => {
    if (!jobId) return null;
    setActiveJobId(jobId);
    setServerSyncEnabled(true);
    const response = await editorService.getEditorData(jobId);
    if (!response) return null;

    // Check job status - handle failed, processing, and completed states
    const status = response.status || 'pending';
    const processingStatuses = ['pending', 'converting', 'transcribing', 'processing'];
    
    const projectName = response.name || response.project_name || response.projectName || null;

    if (status === 'failed') {
      return {
        captionData: null,
        mediaUrl: null,
        fileName: response.filename || response.file_name || null,
        projectName,
        settings: null,
        status: status,
        error: response.error || 'Job processing failed',
        isProcessing: false,
        isFailed: true
      };
    }
    
    if (processingStatuses.includes(status)) {
      return {
        captionData: null,
        mediaUrl: null,
        fileName: response.filename || response.file_name || null,
        projectName,
        settings: null,
        status: status,
        isProcessing: true,
        isFailed: false
      };
    }

    const captionPayload =
      response.caption_data ||
      response.captionData ||
      response.data ||
      response.editor_data ||
      response.captions ||
      null;

    const mediaUrl = response.media_url || response.mediaUrl || response.audio_url || null;
    const fileName = response.filename || response.file_name || null;
    const settings = response.editor_settings || response.settings || null;
    const jobSettings = response.job_settings || response.jobSettings || null;

    if (captionPayload?.skeleton && captionPayload?.words) {
      updateCaptionData(captionPayload, true);
    }

    // Handle translation data
    const translation = response.translation || null;
    const translationDataFromServer = response.translation_data || null;
    const translationRequestedFromServer = response.translation_requested || false;
    
    // Set server translation (original from server)
    if (translation) {
      setServerTranslation(translation);
      setTranslationStatus('completed');
    }
    
    // Set user-editable translation data if available
    if (translationDataFromServer) {
      setTranslationData(translationDataFromServer);
    } else if (translation && translation.words && translation.words.length > 0) {
      // Initialize editable translation from server translation
      const editableTranslation = initializeTranslationFromServer(translation);
      setTranslationData(editableTranslation);
    }
    
    // Set translation requested status
    setTranslationRequested(translationRequestedFromServer);
    if (translationRequestedFromServer && !translation) {
      setTranslationStatus('pending');
    }

    // Get language from response (defaults to 'ar' for Arabic)
    const language = response.language || 'ar';

    // Trial protection flag from backend
    const isTrialProtected = response.is_trial_protected || false;

    return {
      captionData: captionPayload,
      mediaUrl,
      fileName,
      projectName,
      settings,
      jobSettings,
      language,
      status: status,
      isProcessing: false,
      translation,
      translationRequested: translationRequestedFromServer,
      isTrialProtected
    };
  }, [updateCaptionData]);

  const saveToServer = useCallback(async (jobId, data, settings, translationDataToSave = null) => {
    if (!jobId) return null;
    setActiveJobId(jobId);
    setServerSyncEnabled(true);
    const payload = data || captionData;
    const response = await editorService.saveEditorData(jobId, {
      ...payload,
      settings,
      translation_data: translationDataToSave || translationData
    });
    return response;
  }, [captionData, translationData]);

  // O(1) word lookup maps — rebuilt only when the underlying arrays change
  const wordsMapOriginal = useMemo(() => {
    const map = {};
    for (const w of captionData.words) { map[w.id] = w; }
    return map;
  }, [captionData.words]);

  const wordsMapTranslation = useMemo(() => {
    if (!translationData?.words) return null;
    const map = {};
    for (const w of translationData.words) { map[w.id] = w; }
    return map;
  }, [translationData?.words]);

  // Helper function to get word by ID (from original data)
  const getWordById = useCallback((wordId) => {
    return wordsMapOriginal[wordId] || undefined;
  }, [wordsMapOriginal]);
  
  // Helper function to get word by ID (respects isTranslationView)
  const getViewWordById = useCallback((wordId) => {
    if (isTranslationView && wordsMapTranslation) {
      return wordsMapTranslation[wordId] || undefined;
    }
    return wordsMapOriginal[wordId] || undefined;
  }, [isTranslationView, wordsMapTranslation, wordsMapOriginal]);

  // Helper function to get display text based on displayMode
  const getDisplayText = useCallback((word) => {
    if (!word) return '';
    if (displayMode === 'arabizi' && word.arabizi) {
      return word.arabizi;
    }
    return word.text;
  }, [displayMode]);

  // Update a specific word
  const updateWord = useCallback((wordId, updates) => {
    const newWords = captionData.words.map(word =>
      word.id === wordId ? { ...word, ...updates } : word
    );
    updateCaptionData({ ...captionData, words: newWords });
    markWordsAsChanged(wordId);
    // Clear "new" status if text was changed
    if (updates.text !== undefined || updates.arabizi !== undefined) {
      clearNewWordStatus(wordId);
    }
  }, [captionData, updateCaptionData, markWordsAsChanged, clearNewWordStatus]);

  // Update a specific skeleton
  const updateSkeleton = useCallback((skeletonIndex, updates) => {
    const newSkeleton = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex ? { ...skel, ...updates } : skel
    );
    updateCaptionData({ ...captionData, skeleton: newSkeleton });
  }, [captionData, updateCaptionData]);

  // Update skeleton timing with automatic word adjustment
  const updateSkeletonTiming = useCallback((skeletonIndex, newStartTime, newEndTime, options = {}) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton) return;

    const { autoAdjustAdjacent = false, overlapInfo = null } = options;

    // Get the old timing
    const oldStartTime = skeleton.start_time;
    const oldEndTime = skeleton.end_time;
    const oldDuration = oldEndTime - oldStartTime;
    const newDuration = newEndTime - newStartTime;

    if (oldDuration <= 0 || newDuration <= 0) return;

    // Calculate the scaling factor for word timing
    const scaleFactor = newDuration / oldDuration;
    const timeShift = newStartTime - oldStartTime;

    // Get words in this skeleton and adjust their timing proportionally
    const wordIdsInSkeleton = new Set(skeleton.words);
    const newWords = captionData.words.map(word => {
      if (!wordIdsInSkeleton.has(word.id)) return word;

      // Calculate new timing relative to the skeleton
      const relativeStart = word.start_time - oldStartTime;
      const relativeEnd = word.end_time - oldStartTime;

      const adjustedStart = newStartTime + (relativeStart * scaleFactor);
      const adjustedEnd = newStartTime + (relativeEnd * scaleFactor);

      return {
        ...word,
        start_time: adjustedStart,
        end_time: adjustedEnd
      };
    });

    // Update the skeleton
    let newSkeleton = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex ? { ...skel, start_time: newStartTime, end_time: newEndTime } : skel
    );

    // Auto-adjust adjacent skeletons if requested
    if (autoAdjustAdjacent && overlapInfo) {
      if (overlapInfo.type === 'overlap_prev') {
        // Adjust the previous skeleton's end time and its last word
        const prevIndex = overlapInfo.affectedIndex;
        const prevSkeleton = newSkeleton[prevIndex];

        if (prevSkeleton && prevSkeleton.words.length > 0) {
          const lastWordId = prevSkeleton.words[prevSkeleton.words.length - 1];

          // Update previous skeleton end time
          newSkeleton = newSkeleton.map((skel, idx) =>
            idx === prevIndex ? { ...skel, end_time: newStartTime } : skel
          );

          // Update last word of previous skeleton
          const updatedWords = newWords.map(word =>
            word.id === lastWordId ? { ...word, end_time: newStartTime } : word
          );

          updateCaptionData({ skeleton: newSkeleton, words: updatedWords });
          return;
        }
      } else if (overlapInfo.type === 'overlap_next') {
        // Adjust the next skeleton's start time and its first word
        const nextIndex = overlapInfo.affectedIndex;
        const nextSkeleton = newSkeleton[nextIndex];

        if (nextSkeleton && nextSkeleton.words.length > 0) {
          const firstWordId = nextSkeleton.words[0];

          // Update next skeleton start time
          newSkeleton = newSkeleton.map((skel, idx) =>
            idx === nextIndex ? { ...skel, start_time: newEndTime } : skel
          );

          // Update first word of next skeleton
          const updatedWords = newWords.map(word =>
            word.id === firstWordId ? { ...word, start_time: newEndTime } : word
          );

          updateCaptionData({ skeleton: newSkeleton, words: updatedWords });
          return;
        }
      }
    }

    // Update without adjacent adjustment
    updateCaptionData({ skeleton: newSkeleton, words: newWords });
  }, [captionData, updateCaptionData]);

  // Delete a skeleton
  const deleteSkeleton = useCallback((skeletonIndex) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton) return;

    // Remove the skeleton
    const newSkeleton = captionData.skeleton.filter((_, idx) => idx !== skeletonIndex);
    
    // Remove associated words
    const wordIdsToRemove = new Set(skeleton.words);
    const newWords = captionData.words.filter(w => !wordIdsToRemove.has(w.id));

    // Update phrases: remove deleted word IDs and clean up empty phrases
    const newPhrases = (captionData.phrases || []).map(phrase => {
      const hasDeletedWords = phrase.currentWordIds.some(id => wordIdsToRemove.has(id));
      if (hasDeletedWords) {
        return {
          ...phrase,
          currentWordIds: phrase.currentWordIds.filter(id => !wordIdsToRemove.has(id)),
          originalWordIds: phrase.originalWordIds.filter(id => !wordIdsToRemove.has(id)),
          originalWords: phrase.originalWords.filter(w => !wordIdsToRemove.has(w.id))
        };
      }
      return phrase;
    }).filter(phrase => phrase.currentWordIds.length > 0);

    updateCaptionData({ skeleton: newSkeleton, words: newWords, phrases: newPhrases });
  }, [captionData, updateCaptionData]);

  // Split skeleton at word index
  const splitSkeleton = useCallback((skeletonIndex, wordIndexInSkeleton) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton || wordIndexInSkeleton <= 0 || wordIndexInSkeleton >= skeleton.words.length) return;

    const wordsBeforeSplit = skeleton.words.slice(0, wordIndexInSkeleton);
    const wordsAfterSplit = skeleton.words.slice(wordIndexInSkeleton);

    // Calculate bounds from actual word timestamps
    const bounds1 = calculateSkeletonBounds(wordsBeforeSplit, captionData.words, skeleton.start_time, skeleton.end_time);
    const bounds2 = calculateSkeletonBounds(wordsAfterSplit, captionData.words, skeleton.start_time, skeleton.end_time);

    const newSkeleton1 = {
      ...skeleton,
      words: wordsBeforeSplit,
      start_time: bounds1.start_time,
      end_time: bounds1.end_time
    };

    const newSkeleton2 = {
      id: generateId('s'),
      words: wordsAfterSplit,
      start_time: bounds2.start_time,
      end_time: bounds2.end_time,
      size: skeleton.size || 'medium',
      positionY: skeleton.positionY,
      emphasize: skeleton.emphasize || false,
      disabled: skeleton.disabled || false
    };

    const newSkeletons = [
      ...captionData.skeleton.slice(0, skeletonIndex),
      newSkeleton1,
      newSkeleton2,
      ...captionData.skeleton.slice(skeletonIndex + 1)
    ];

    updateCaptionData({ ...captionData, skeleton: newSkeletons });
  }, [captionData, updateCaptionData]);

  // Merge skeleton with previous
  const mergeWithPrevious = useCallback((skeletonIndex) => {
    if (skeletonIndex <= 0) return;

    const prevSkeleton = captionData.skeleton[skeletonIndex - 1];
    const currentSkeleton = captionData.skeleton[skeletonIndex];

    // Combine word arrays
    const mergedWords = [...prevSkeleton.words, ...currentSkeleton.words];

    // Calculate bounds from actual word timestamps
    const bounds = calculateSkeletonBounds(mergedWords, captionData.words, prevSkeleton.start_time, currentSkeleton.end_time);

    const mergedSkeleton = {
      ...prevSkeleton,
      words: mergedWords,
      start_time: bounds.start_time,
      end_time: bounds.end_time
    };

    const newSkeletons = [
      ...captionData.skeleton.slice(0, skeletonIndex - 1),
      mergedSkeleton,
      ...captionData.skeleton.slice(skeletonIndex + 1)
    ];

    updateCaptionData({ ...captionData, skeleton: newSkeletons });
  }, [captionData, updateCaptionData]);

  // Add a new word to a skeleton
  const addWord = useCallback((skeletonIndex, afterWordId, newWordText) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton) return;

    const wordIndex = skeleton.words.indexOf(afterWordId);
    const afterWord = captionData.words.find(w => w.id === afterWordId);

    const newWordId = generateId('w');
    const newWord = {
      id: newWordId,
      text: newWordText || 'كلمة',
      type: 'word',
      start_time: afterWord?.end_time || 0,
      end_time: (afterWord?.end_time || 0) + 0.5,
      highlightColor: null,
      break: false
    };

    // Insert word ID into skeleton
    const newWordIds = [
      ...skeleton.words.slice(0, wordIndex + 1),
      newWordId,
      ...skeleton.words.slice(wordIndex + 1)
    ];

    // Create updated words array with new word inserted in correct timestamp order
    const updatedWords = [...captionData.words, newWord].sort((a, b) =>
      (a.start_time || 0) - (b.start_time || 0)
    );

    // Get first and last word IDs to update skeleton timing
    const firstWordId = newWordIds[0];
    const lastWordId = newWordIds[newWordIds.length - 1];

    // Find first and last words of this skeleton
    const firstWord = updatedWords.find(w => w.id === firstWordId);
    const lastWord = updatedWords.find(w => w.id === lastWordId);

    const newSkeleton = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex ? {
        ...skel,
        words: newWordIds,
        start_time: firstWord?.start_time ?? skel.start_time,
        end_time: lastWord?.end_time ?? skel.end_time
      } : skel
    );

    updateCaptionData({
      skeleton: newSkeleton,
      words: updatedWords
    });
    
    // Trigger animation feedback for the new word
    markWordsAsChanged(newWordId);
    // Mark word as new (will have different color until edited)
    markWordsAsNew(newWordId);
  }, [captionData, updateCaptionData, markWordsAsChanged, markWordsAsNew]);

  // Split a word containing spaces into multiple words
  // splitByArabizi: if true, use arabizi field as primary split source; otherwise use text field
  const splitWord = useCallback((skeletonIndex, wordId, splitByArabizi = false) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton) return { success: false, error: 'Skeleton not found' };

    const wordIndex = skeleton.words.indexOf(wordId);
    if (wordIndex === -1) return { success: false, error: 'Word not in skeleton' };

    const word = captionData.words.find(w => w.id === wordId);
    if (!word) return { success: false, error: 'Word not found' };

    // Get both text fields
    const arabicText = (word.text || '').trim();
    const arabiziText = (word.arabizi || '').trim();

    // Determine primary text to split based on mode
    const primaryText = splitByArabizi ? (arabiziText || arabicText) : arabicText;
    const secondaryText = splitByArabizi ? arabicText : arabiziText;

    // Split primary text by whitespace
    const primaryParts = primaryText.split(/\s+/).filter(s => s.length > 0);

    if (primaryParts.length <= 1) {
      return { success: false, error: 'Word does not contain splittable spaces' };
    }

    // Try to split secondary text - check if it has the same number of parts
    const secondaryParts = secondaryText ? secondaryText.split(/\s+/).filter(s => s.length > 0) : [];
    const hasMatchingSecondary = secondaryParts.length === primaryParts.length;

    // Calculate total character count for proportional timing (based on primary text)
    const totalChars = primaryParts.reduce((sum, s) => sum + s.length, 0);
    const wordDuration = (word.end_time || 0) - (word.start_time || 0);
    const wordStartTime = word.start_time || 0;
    const wordEndTime = word.end_time || 0;

    // Create new words with proportional timestamps
    const newWords = [];
    const newWordIds = [];
    let currentTime = wordStartTime;

    primaryParts.forEach((primaryPart, index) => {
      const charRatio = primaryPart.length / totalChars;
      const subDuration = wordDuration * charRatio;

      const newWordId = generateId('w');
      newWordIds.push(newWordId);

      const newWord = {
        id: newWordId,
        // Set text and arabizi based on which was primary
        text: splitByArabizi
          ? (hasMatchingSecondary ? secondaryParts[index] : (index === 0 ? arabicText : primaryPart))
          : primaryPart,
        type: word.type || 'word',
        start_time: currentTime,
        end_time: currentTime + subDuration,
        highlightColor: index === 0 ? word.highlightColor : null,
        break: index === primaryParts.length - 1 ? word.break : false,
        important: index === 0 ? word.important : false,
      };

      // Set arabizi field
      if (splitByArabizi) {
        // Primary was arabizi, so set it directly
        newWord.arabizi = primaryPart;
      } else {
        // Primary was arabic, set arabizi from secondary if matching
        if (hasMatchingSecondary) {
          newWord.arabizi = secondaryParts[index];
        } else if (index === 0 && arabiziText) {
          newWord.arabizi = arabiziText;
        }
      }

      newWords.push(newWord);
      currentTime += subDuration;
    });

    // Ensure last word ends exactly at original end time (avoid floating point drift)
    if (newWords.length > 0) {
      newWords[newWords.length - 1].end_time = wordEndTime;
    }

    // Update skeleton's word array: replace old wordId with new wordIds
    const newSkeletonWords = [
      ...skeleton.words.slice(0, wordIndex),
      ...newWordIds,
      ...skeleton.words.slice(wordIndex + 1)
    ];

    // Build updated words array
    const allUpdatedWords = [
      ...captionData.words.filter(w => w.id !== wordId),
      ...newWords
    ].sort((a, b) => (a.start_time || 0) - (b.start_time || 0));

    // Recalculate skeleton bounds
    const bounds = calculateSkeletonBounds(newSkeletonWords, allUpdatedWords, skeleton.start_time, skeleton.end_time);

    // Update skeleton
    const newSkeletons = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex
        ? { ...skel, words: newSkeletonWords, start_time: bounds.start_time, end_time: bounds.end_time }
        : skel
    );

    // Update phrases if the word was part of a phrase
    const newPhrases = (captionData.phrases || []).map(phrase => {
      if (phrase.currentWordIds.includes(wordId)) {
        const wordPos = phrase.currentWordIds.indexOf(wordId);
        return {
          ...phrase,
          currentWordIds: [
            ...phrase.currentWordIds.slice(0, wordPos),
            ...newWordIds,
            ...phrase.currentWordIds.slice(wordPos + 1)
          ],
          originalWordIds: phrase.originalWordIds.includes(wordId)
            ? [
                ...phrase.originalWordIds.slice(0, phrase.originalWordIds.indexOf(wordId)),
                ...newWordIds,
                ...phrase.originalWordIds.slice(phrase.originalWordIds.indexOf(wordId) + 1)
              ]
            : phrase.originalWordIds
        };
      }
      return phrase;
    });

    updateCaptionData({
      skeleton: newSkeletons,
      words: allUpdatedWords,
      phrases: newPhrases
    });

    return { success: true, newWordIds };
  }, [captionData, updateCaptionData]);

  // Remove a word from skeleton
  const removeWord = useCallback((skeletonIndex, wordId) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton || skeleton.words.length <= 1) return; // Don't remove last word

    const newWordIds = skeleton.words.filter(id => id !== wordId);
    const newWords = captionData.words.filter(w => w.id !== wordId);

    // Update skeleton timing based on remaining words
    const firstWord = newWords.find(w => w.id === newWordIds[0]);
    const lastWord = newWords.find(w => w.id === newWordIds[newWordIds.length - 1]);

    const newSkeleton = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex ? {
        ...skel,
        words: newWordIds,
        start_time: firstWord?.start_time || skel.start_time,
        end_time: lastWord?.end_time || skel.end_time
      } : skel
    );

    // Update phrases: remove the word from any phrase's currentWordIds
    // If a phrase has no more words, remove it entirely
    let newPhrases = (captionData.phrases || []).map(phrase => {
      if (phrase.currentWordIds.includes(wordId)) {
        const updatedCurrentWordIds = phrase.currentWordIds.filter(id => id !== wordId);
        // Also update originalWordIds if the removed word was an original
        const updatedOriginalWordIds = phrase.originalWordIds.filter(id => id !== wordId);
        const updatedOriginalWords = phrase.originalWords.filter(w => w.id !== wordId);
        
        return {
          ...phrase,
          currentWordIds: updatedCurrentWordIds,
          originalWordIds: updatedOriginalWordIds,
          originalWords: updatedOriginalWords
        };
      }
      return phrase;
    }).filter(phrase => phrase.currentWordIds.length > 0); // Remove empty phrases

    updateCaptionData({ skeleton: newSkeleton, words: newWords, phrases: newPhrases });
  }, [captionData, updateCaptionData]);

  // Merge two adjacent words into one
  // direction: 'previous' merges current word with the word before it
  // direction: 'next' merges current word with the word after it
  const mergeWords = useCallback((skeletonIndex, wordId, direction) => {
    const skeleton = captionData.skeleton[skeletonIndex];
    if (!skeleton) return { success: false, error: 'Skeleton not found' };

    const wordIndexInSkeleton = skeleton.words.indexOf(wordId);
    if (wordIndexInSkeleton === -1) return { success: false, error: 'Word not in skeleton' };

    const currentWord = captionData.words.find(w => w.id === wordId);
    if (!currentWord) return { success: false, error: 'Word not found' };

    let adjacentWordId;
    let adjacentWordIndex;

    if (direction === 'previous') {
      if (wordIndexInSkeleton === 0) return { success: false, error: 'No previous word to merge with' };
      adjacentWordIndex = wordIndexInSkeleton - 1;
      adjacentWordId = skeleton.words[adjacentWordIndex];
    } else if (direction === 'next') {
      if (wordIndexInSkeleton === skeleton.words.length - 1) return { success: false, error: 'No next word to merge with' };
      adjacentWordIndex = wordIndexInSkeleton + 1;
      adjacentWordId = skeleton.words[adjacentWordIndex];
    } else {
      return { success: false, error: 'Invalid direction' };
    }

    const adjacentWord = captionData.words.find(w => w.id === adjacentWordId);
    if (!adjacentWord) return { success: false, error: 'Adjacent word not found' };

    // Determine which word comes first chronologically
    const firstWord = direction === 'previous' ? adjacentWord : currentWord;
    const secondWord = direction === 'previous' ? currentWord : adjacentWord;

    // Merge text - for Arabic (RTL), the visual order is right-to-left
    // So when merging, we want: previousWord.text + " " + currentWord.text
    // This will display correctly in RTL as: currentWord previousWord
    const mergedText = (firstWord.text || '') + ' ' + (secondWord.text || '');
    
    // Merge arabizi if present
    let mergedArabizi = null;
    if (firstWord.arabizi || secondWord.arabizi) {
      mergedArabizi = ((firstWord.arabizi || firstWord.text || '') + ' ' + (secondWord.arabizi || secondWord.text || '')).trim();
    }

    // Merge timestamps - take earliest start and latest end
    const mergedStartTime = Math.min(
      firstWord.start_time ?? Infinity,
      secondWord.start_time ?? Infinity
    );
    const mergedEndTime = Math.max(
      firstWord.end_time ?? 0,
      secondWord.end_time ?? 0
    );

    // The merged word keeps the ID of the first word (chronologically)
    const mergedWordId = firstWord.id;
    const removedWordId = secondWord.id;

    // Create the merged word
    const mergedWord = {
      ...firstWord,
      text: mergedText.trim(),
      arabizi: mergedArabizi,
      start_time: mergedStartTime === Infinity ? 0 : mergedStartTime,
      end_time: mergedEndTime,
      // Keep highlight if either word had one
      highlightColor: firstWord.highlightColor || secondWord.highlightColor,
      // Keep important if either word was important
      important: firstWord.important || secondWord.important,
      // Keep break only from the second word (since first word's break position is now internal)
      break: secondWord.break || false
    };

    // Update words array - replace first word with merged, remove second word
    const newWords = captionData.words
      .map(w => w.id === mergedWordId ? mergedWord : w)
      .filter(w => w.id !== removedWordId);

    // Update skeleton's word list - remove the merged-away word
    const newSkeletonWords = skeleton.words.filter(id => id !== removedWordId);

    // Recalculate skeleton bounds
    const bounds = calculateSkeletonBounds(newSkeletonWords, newWords, skeleton.start_time, skeleton.end_time);

    // Update skeleton
    const newSkeletons = captionData.skeleton.map((skel, idx) =>
      idx === skeletonIndex
        ? { ...skel, words: newSkeletonWords, start_time: bounds.start_time, end_time: bounds.end_time }
        : skel
    );

    // Update phrases - remove the merged-away word from any phrase
    const newPhrases = (captionData.phrases || []).map(phrase => {
      const hasRemovedWord = phrase.currentWordIds.includes(removedWordId);
      if (hasRemovedWord) {
        return {
          ...phrase,
          currentWordIds: phrase.currentWordIds.filter(id => id !== removedWordId),
          originalWordIds: phrase.originalWordIds.filter(id => id !== removedWordId),
          originalWords: phrase.originalWords.filter(w => w.id !== removedWordId)
        };
      }
      return phrase;
    }).filter(phrase => phrase.currentWordIds.length > 0);

    updateCaptionData({
      skeleton: newSkeletons,
      words: newWords,
      phrases: newPhrases
    });

    return { success: true, mergedWordId };
  }, [captionData, updateCaptionData]);

  // Merge multiple words (in order) into one word
  const mergeMultipleWords = useCallback((skeletonIndex, wordIds) => {
    if (!wordIds || wordIds.length < 2) return { success: false, error: 'Need at least 2 words' };

    const wordIdSet = new Set(wordIds);
    const skeletonIndices = captionData.skeleton.reduce((acc, skel, idx) => {
      if (skel.words.some(id => wordIdSet.has(id))) acc.push(idx);
      return acc;
    }, []);

    if (skeletonIndices.length === 0) return { success: false, error: 'Skeleton not found' };

    let workingData = captionData;
    let targetSkeletonIndex = skeletonIndices[0];

    if (skeletonIndices.length > 1) {
      const sortedIndices = [...skeletonIndices].sort((a, b) => a - b);
      targetSkeletonIndex = sortedIndices[0];

      const combinedWords = [];
      const skeletonsToRemove = new Set();

      sortedIndices.forEach((idx, i) => {
        const skel = workingData.skeleton[idx];
        if (skel) {
          combinedWords.push(...skel.words);
          if (i > 0) skeletonsToRemove.add(idx);
        }
      });

      const firstSkel = workingData.skeleton[targetSkeletonIndex];
      const lastSkel = workingData.skeleton[sortedIndices[sortedIndices.length - 1]];

      const mergedSkeleton = {
        ...firstSkel,
        words: combinedWords,
        start_time: firstSkel.start_time,
        end_time: lastSkel.end_time
      };

      const newSkeletons = workingData.skeleton
        .map((skel, idx) => (idx === targetSkeletonIndex ? mergedSkeleton : skel))
        .filter((_, idx) => !skeletonsToRemove.has(idx));

      workingData = {
        ...workingData,
        skeleton: newSkeletons
      };
    }

    const skeleton = workingData.skeleton[targetSkeletonIndex];
    if (!skeleton) return { success: false, error: 'Skeleton not found' };

    const orderedIds = skeleton.words.filter(id => wordIdSet.has(id));
    if (orderedIds.length < 2) return { success: false, error: 'Words not in skeleton' };

    const wordsById = new Map(workingData.words.map(w => [w.id, w]));
    const orderedWords = orderedIds.map(id => wordsById.get(id)).filter(Boolean);
    if (orderedWords.length < 2) return { success: false, error: 'Words not found' };

    const mergedText = orderedWords.map(w => w.text || '').join(' ').trim();
    const mergedArabizi = orderedWords.some(w => w.arabizi)
      ? orderedWords.map(w => (w.arabizi || w.text || '')).join(' ').trim()
      : null;

    const mergedStart = Math.min(...orderedWords.map(w => w.start_time ?? Infinity));
    const mergedEnd = Math.max(...orderedWords.map(w => w.end_time ?? 0));
    const mergedWordId = orderedWords[0].id;
    const removedIds = new Set(orderedIds.slice(1));

    const mergedWord = {
      ...orderedWords[0],
      text: mergedText,
      arabizi: mergedArabizi,
      start_time: mergedStart === Infinity ? 0 : mergedStart,
      end_time: mergedEnd,
      highlightColor: orderedWords.find(w => w.highlightColor)?.highlightColor || null,
      important: orderedWords.some(w => w.important),
      break: orderedWords[orderedWords.length - 1].break || false
    };

    const newWords = workingData.words
      .map(w => (w.id === mergedWordId ? mergedWord : w))
      .filter(w => !removedIds.has(w.id));

    const newSkeletonWords = skeleton.words.filter(id => !removedIds.has(id));
    const bounds = calculateSkeletonBounds(newSkeletonWords, newWords, skeleton.start_time, skeleton.end_time);
    const newSkeletons = workingData.skeleton.map((skel, idx) =>
      idx === targetSkeletonIndex
        ? { ...skel, words: newSkeletonWords, start_time: bounds.start_time, end_time: bounds.end_time }
        : skel
    );

    const newPhrases = (workingData.phrases || []).map(phrase => {
      if (phrase.currentWordIds.some(id => removedIds.has(id))) {
        return {
          ...phrase,
          currentWordIds: phrase.currentWordIds.filter(id => !removedIds.has(id)),
          originalWordIds: phrase.originalWordIds.filter(id => !removedIds.has(id)),
          originalWords: phrase.originalWords.filter(w => !removedIds.has(w.id))
        };
      }
      return phrase;
    }).filter(phrase => phrase.currentWordIds.length > 0);

    updateCaptionData({
      skeleton: newSkeletons,
      words: newWords,
      phrases: newPhrases
    });

    return { success: true, mergedWordId };
  }, [captionData, updateCaptionData]);

  // Merge multiple consecutive words and split them by spaces with new timing
  // This is used for timing adjustment when words are out of sync
  // skeletonIndices can be a single index or an array of indices (for cross-skeleton selection)
  const mergeAndSplitWords = useCallback((skeletonIndices, wordIds, newStartTime, newEndTime) => {
    if (!wordIds || wordIds.length < 2) return { success: false, error: 'Need at least 2 words to merge' };
    
    // Normalize skeletonIndices to array
    const indices = Array.isArray(skeletonIndices) ? skeletonIndices : [skeletonIndices];
    if (indices.length === 0) return { success: false, error: 'No skeleton indices provided' };
    
    // If multiple skeletons are involved, merge them first
    let workingData = { ...captionData };
    let targetSkeletonIndex = indices[0];
    
    if (indices.length > 1) {
      // Sort indices in ascending order
      const sortedIndices = [...indices].sort((a, b) => a - b);
      targetSkeletonIndex = sortedIndices[0];
      
      // Merge all skeletons into the first one
      // Combine all words from all affected skeletons
      const combinedWords = [];
      const skeletonsToRemove = new Set();
      
      sortedIndices.forEach((idx, i) => {
        const skel = workingData.skeleton[idx];
        if (skel) {
          combinedWords.push(...skel.words);
          if (i > 0) skeletonsToRemove.add(idx);
        }
      });
      
      // Create merged skeleton
      const firstSkel = workingData.skeleton[targetSkeletonIndex];
      const lastSkel = workingData.skeleton[sortedIndices[sortedIndices.length - 1]];
      
      const mergedSkeleton = {
        ...firstSkel,
        words: combinedWords,
        start_time: firstSkel.start_time,
        end_time: lastSkel.end_time
      };
      
      // Update skeletons array: replace first, remove others
      const newSkeletons = workingData.skeleton
        .map((skel, idx) => idx === targetSkeletonIndex ? mergedSkeleton : skel)
        .filter((_, idx) => !skeletonsToRemove.has(idx));
      
      workingData = {
        ...workingData,
        skeleton: newSkeletons
      };
      
      // Recalculate target index after removal
      // (it stays the same since we're keeping the first one)
    }
    
    const skeleton = workingData.skeleton[targetSkeletonIndex];
    if (!skeleton) return { success: false, error: 'Skeleton not found' };

    // Verify all words are in the skeleton and get their positions
    const wordPositions = wordIds.map(id => skeleton.words.indexOf(id));
    if (wordPositions.some(pos => pos === -1)) {
      return { success: false, error: 'Some words not found in skeleton' };
    }

    // Sort by position and check consecutive
    const sortedPositions = [...wordPositions].sort((a, b) => a - b);
    for (let i = 1; i < sortedPositions.length; i++) {
      if (sortedPositions[i] !== sortedPositions[i - 1] + 1) {
        return { success: false, error: 'Words must be consecutive' };
      }
    }

    // Get word data in order
    const orderedWordIds = sortedPositions.map(pos => skeleton.words[pos]);
    const words = orderedWordIds.map(id => workingData.words.find(w => w.id === id)).filter(Boolean);
    
    if (words.length !== wordIds.length) {
      return { success: false, error: 'Some word data not found' };
    }

    // Combine text and arabizi
    const combinedText = words.map(w => w.text || '').join(' ').trim();
    const combinedArabizi = words.some(w => w.arabizi)
      ? words.map(w => w.arabizi || w.text || '').join(' ').trim()
      : null;

    // Split by whitespace
    const textParts = combinedText.split(/\s+/).filter(s => s.length > 0);
    const arabiziParts = combinedArabizi 
      ? combinedArabizi.split(/\s+/).filter(s => s.length > 0) 
      : [];

    if (textParts.length === 0) {
      return { success: false, error: 'No text to split' };
    }

    // Calculate proportional timing based on character count
    const totalChars = textParts.reduce((sum, s) => sum + s.length, 0);
    const totalDuration = newEndTime - newStartTime;

    // Create new words with proportional timestamps
    const newWords = [];
    const newWordIds = [];
    let currentTime = newStartTime;

    textParts.forEach((textPart, index) => {
      const charRatio = textPart.length / totalChars;
      const wordDuration = totalDuration * charRatio;

      const newWordId = generateId('w');
      newWordIds.push(newWordId);

      const newWord = {
        id: newWordId,
        text: textPart,
        type: 'word',
        start_time: currentTime,
        end_time: currentTime + wordDuration,
        highlightColor: index === 0 ? words[0].highlightColor : null,
        break: index === textParts.length - 1 ? words[words.length - 1].break : false,
        important: index === 0 ? words[0].important : false,
      };

      // Set arabizi if available and parts match
      if (arabiziParts.length === textParts.length) {
        newWord.arabizi = arabiziParts[index];
      } else if (index === 0 && combinedArabizi) {
        newWord.arabizi = combinedArabizi;
      }

      newWords.push(newWord);
      currentTime += wordDuration;
    });

    // Ensure last word ends exactly at newEndTime (avoid floating point drift)
    if (newWords.length > 0) {
      newWords[newWords.length - 1].end_time = newEndTime;
    }

    // Update skeleton's word array: replace old wordIds with new wordIds
    const insertPosition = sortedPositions[0];
    const removeCount = sortedPositions.length;
    const newSkeletonWords = [
      ...skeleton.words.slice(0, insertPosition),
      ...newWordIds,
      ...skeleton.words.slice(insertPosition + removeCount)
    ];

    // Build updated words array
    const oldWordIdSet = new Set(orderedWordIds);
    const allUpdatedWords = [
      ...workingData.words.filter(w => !oldWordIdSet.has(w.id)),
      ...newWords
    ].sort((a, b) => (a.start_time || 0) - (b.start_time || 0));

    // Recalculate skeleton bounds
    const bounds = calculateSkeletonBounds(newSkeletonWords, allUpdatedWords, skeleton.start_time, skeleton.end_time);

    // Update skeleton
    const newSkeletons = workingData.skeleton.map((skel, idx) =>
      idx === targetSkeletonIndex
        ? { ...skel, words: newSkeletonWords, start_time: bounds.start_time, end_time: bounds.end_time }
        : skel
    );

    // Update phrases if any of the old words were part of a phrase
    const newPhrases = (workingData.phrases || []).map(phrase => {
      const hasOldWords = phrase.currentWordIds.some(id => oldWordIdSet.has(id));
      if (hasOldWords) {
        // Replace old word IDs with new word IDs in phrases
        const updatedCurrentWordIds = [];
        phrase.currentWordIds.forEach(id => {
          if (oldWordIdSet.has(id)) {
            // Only add newWordIds once (for the first old word found)
            if (id === orderedWordIds[0]) {
              updatedCurrentWordIds.push(...newWordIds);
            }
            // Skip other old word IDs
          } else {
            updatedCurrentWordIds.push(id);
          }
        });

        return {
          ...phrase,
          currentWordIds: updatedCurrentWordIds,
          originalWordIds: phrase.originalWordIds.filter(id => !oldWordIdSet.has(id)),
          originalWords: phrase.originalWords.filter(w => !oldWordIdSet.has(w.id))
        };
      }
      return phrase;
    }).filter(phrase => phrase.currentWordIds.length > 0);

    updateCaptionData({
      skeleton: newSkeletons,
      words: allUpdatedWords,
      phrases: newPhrases
    });

    return { success: true, newWordIds };
  }, [captionData, updateCaptionData]);

  // Reset a single skeleton to defaults
  const resetSkeleton = useCallback((skeletonIndex) => {
    updateSkeleton(skeletonIndex, {
      size: 'medium',
      positionY: null,
      emphasize: false
    });
  }, [updateSkeleton]);

  // Reset all skeletons to defaults
  const resetAllSkeletons = useCallback(() => {
    const newSkeleton = captionData.skeleton.map(skel => ({
      ...skel,
      size: 'medium',
      positionY: null,
      emphasize: false
    }));
    updateCaptionData({ ...captionData, skeleton: newSkeleton });
  }, [captionData, updateCaptionData]);

  // Add new empty skeleton
  const addSkeleton = useCallback((afterIndex) => {
    const prevSkeleton = captionData.skeleton[afterIndex];
    const nextSkeleton = captionData.skeleton[afterIndex + 1];
    
    const startTime = prevSkeleton?.end_time || 0;
    const endTime = nextSkeleton?.start_time || startTime + 1;

    const newWordId = generateId('w');
    const newWord = {
      id: newWordId,
      text: 'كلمة',
      type: 'word',
      start_time: startTime,
      end_time: endTime,
      highlightColor: null,
      break: false
    };

    const newSkel = {
      id: generateId('s'),
      words: [newWordId],
      start_time: startTime,
      end_time: endTime,
      size: 'medium',
      positionY: null,
      emphasize: false,
      disabled: false
    };

    const newSkeletons = [
      ...captionData.skeleton.slice(0, afterIndex + 1),
      newSkel,
      ...captionData.skeleton.slice(afterIndex + 1)
    ];

    // Insert word in proper timestamp order
    const updatedWords = [...captionData.words, newWord].sort((a, b) =>
      (a.start_time || 0) - (b.start_time || 0)
    );

    updateCaptionData({
      skeleton: newSkeletons,
      words: updatedWords
    });
    
    // Mark the new word for animation and as new (different color until edited)
    markWordsAsChanged(newWordId);
    markWordsAsNew(newWordId);
  }, [captionData, updateCaptionData, markWordsAsChanged, markWordsAsNew]);

  // Reorganize skeletons based on max words or characters per chunk
  // This completely rebuilds all skeletons from scratch using only words
  // Options:
  //   - mode: 'words' or 'characters'
  //   - maxWords: max words per chunk (when mode is 'words')
  //   - maxChars: max characters per chunk (when mode is 'characters')
  //   - separateScripts: keep Latin phrase words separate from Arabic words
  //   - silenceAware: split chunks when silence tokens appear
  //   - punctuationAware: split chunks when punctuation appears
  const reorganizeSkeletons = useCallback((options = {}) => {
    const currentCaptionData = captionDataRef.current;
    const {
      mode = 'words',
      maxWords = 8,
      maxChars = 50,
      separateScripts = false,
      silenceAware = false,
      punctuationAware = false,
      splitBySpeaker = false
    } = options;

    // Prepare ordered tokens to optionally segment by silences
    const orderedTokens = currentCaptionData.words
      .filter(w => w && (w.type || w.text))
      .sort((a, b) => (a.start_time || 0) - (b.start_time || 0));

    const phrases = currentCaptionData.phrases || [];

    // Build local word-to-phrase lookup for O(1) access
    const localWordToPhraseMap = {};
    for (const phrase of phrases) {
      if (phrase.currentWordIds) {
        for (const wid of phrase.currentWordIds) {
          localWordToPhraseMap[wid] = phrase;
        }
      }
    }

    // Helper to check if a word is in a Latin phrase (code_switched alternative active)
    const isLatinPhraseWord = (wordId) => {
      const phrase = localWordToPhraseMap[wordId];
      return phrase?.activeAlternativeId === 'alt_code_switched';
    };

    // Helper to get the phrase a word belongs to (if any)
    const getPhraseForWord = (wordId) => {
      return localWordToPhraseMap[wordId] || null;
    };

    // Helper to get character count of a chunk
    const getChunkCharCount = (chunk) => {
      return chunk.reduce((sum, w) => sum + (w.text?.length || 0), 0);
    };

    // Helper to check if chunk is full based on mode
    const isChunkFull = (chunk, nextWord) => {
      if (mode === 'characters') {
        const currentChars = getChunkCharCount(chunk);
        const nextWordChars = nextWord?.text?.length || 0;
        return currentChars + nextWordChars > maxChars;
      } else {
        return chunk.length >= maxWords;
      }
    };

    // Helper to finalize a chunk into a skeleton
    const finalizeChunk = (chunk) => {
      if (chunk.length === 0) return null;
      const firstWord = chunk[0];
      const lastWord = chunk[chunk.length - 1];
      return {
        id: generateId('s'),
        words: chunk.map(w => w.id),
        start_time: firstWord.start_time || 0,
        end_time: lastWord.end_time || (firstWord.start_time || 0) + 1,
        size: 'medium',
        positionY: null,
        emphasize: false,
        disabled: false
      };
    };

    const buildSkeletonsForWords = (words) => {
      const skeletons = [];
      if (words.length === 0) return skeletons;

      // If splitBySpeaker is enabled, first segment words by speaker,
      // then apply normal chunking to each speaker segment.
      const speakerSegments = splitBySpeaker ? (() => {
        const segments = [];
        let currentSpeaker = null;
        let currentSegment = [];
        for (const word of words) {
          const speaker = word.speaker || null;
          if (speaker !== currentSpeaker && currentSegment.length > 0) {
            segments.push(currentSegment);
            currentSegment = [];
          }
          currentSegment.push(word);
          currentSpeaker = speaker;
        }
        if (currentSegment.length > 0) segments.push(currentSegment);
        return segments;
      })() : [words];

      for (const segmentWords of speakerSegments) {
        if (separateScripts) {
          // Build chunks respecting script boundaries AND keeping phrases intact
          let currentChunk = [];
          let currentIsLatin = null;
          let i = 0;

          while (i < segmentWords.length) {
            const word = segmentWords[i];
            const wordIsLatin = isLatinPhraseWord(word.id);
            const phrase = wordIsLatin ? getPhraseForWord(word.id) : null;

            // If this is a Latin phrase word, we need to keep the entire phrase together
            if (wordIsLatin && phrase) {
              const phraseWordIds = new Set(phrase.currentWordIds);
              const phraseWords = [];

              let j = i;
              while (j < segmentWords.length && phraseWordIds.has(segmentWords[j].id)) {
                phraseWords.push(segmentWords[j]);
                j++;
              }

              const scriptChanged = currentIsLatin !== null && currentIsLatin !== true;
              const chunkWouldOverflow = currentChunk.length > 0 && isChunkFull(currentChunk, word);

              if (currentChunk.length > 0 && (scriptChanged || chunkWouldOverflow)) {
                const skeleton = finalizeChunk(currentChunk);
                if (skeleton) skeletons.push(skeleton);
                currentChunk = [];
              }

              currentChunk.push(...phraseWords);
              currentIsLatin = true;
              i = j;
            } else {
              const scriptChanged = currentIsLatin !== null && wordIsLatin !== currentIsLatin;
              const chunkFull = currentChunk.length > 0 && isChunkFull(currentChunk, word);

              if (currentChunk.length > 0 && (scriptChanged || chunkFull)) {
                const skeleton = finalizeChunk(currentChunk);
                if (skeleton) skeletons.push(skeleton);
                currentChunk = [];
              }

              currentChunk.push(word);
              currentIsLatin = wordIsLatin;
              i++;
            }
          }

          const lastSkeleton = finalizeChunk(currentChunk);
          if (lastSkeleton) skeletons.push(lastSkeleton);
        } else {
          // Simple chunking without script separation
          if (mode === 'characters') {
            let currentChunk = [];

            for (const word of segmentWords) {
              if (currentChunk.length > 0 && isChunkFull(currentChunk, word)) {
                const skeleton = finalizeChunk(currentChunk);
                if (skeleton) skeletons.push(skeleton);
                currentChunk = [];
              }
              currentChunk.push(word);
            }

            const lastSkeleton = finalizeChunk(currentChunk);
            if (lastSkeleton) skeletons.push(lastSkeleton);
          } else {
            for (let i = 0; i < segmentWords.length; i += maxWords) {
              const chunkWords = segmentWords.slice(i, i + maxWords);
              const skeleton = finalizeChunk(chunkWords);
              if (skeleton) skeletons.push(skeleton);
            }
          }
        }
      }

      return skeletons;
    };

    // Create new skeletons
    const newSkeletons = [];
    let currentSegment = [];

    if (silenceAware || punctuationAware) {
      for (let i = 0; i < orderedTokens.length; i++) {
        const token = orderedTokens[i];
        
        // Split at silence if silenceAware
        if (silenceAware && token.type === 'silence') {
          if (currentSegment.length > 0) {
            newSkeletons.push(...buildSkeletonsForWords(currentSegment));
            currentSegment = [];
          }
          continue;
        }

        // Skip punctuation tokens (they'll be attached later)
        if (token.type === 'punctuation') {
          // If punctuationAware, split after the punctuation
          if (punctuationAware && currentSegment.length > 0) {
            newSkeletons.push(...buildSkeletonsForWords(currentSegment));
            currentSegment = [];
          }
          continue;
        }

        if (token.type === 'word' || (!token.type && token.text)) {
          currentSegment.push(token);
        }
      }

      if (currentSegment.length > 0) {
        newSkeletons.push(...buildSkeletonsForWords(currentSegment));
      }
    } else {
      // Get only actual words (not silences or punctuation), sorted by start_time
      const allWords = orderedTokens.filter(w => w.type === 'word' || (!w.type && w.text));
      newSkeletons.push(...buildSkeletonsForWords(allWords));
    }

    if (newSkeletons.length === 0) return;

    // Insert punctuation into skeletons after their attached words
    const punctuationWords = currentCaptionData.words.filter(w => w.type === 'punctuation');
    const usedPunctuationIds = new Set();

    for (const skeleton of newSkeletons) {
      const wordsInSkeleton = skeleton.words.map(id => currentCaptionData.words.find(w => w.id === id)).filter(Boolean);
      const newWordIds = [];

      for (let i = 0; i < wordsInSkeleton.length; i++) {
        const word = wordsInSkeleton[i];
        newWordIds.push(word.id);

        // Find punctuation that attaches to this word (attaches_to: 'previous')
        // Punctuation typically has the same or very close start_time to its attached word's end_time
        const attachedPunct = punctuationWords.find(p => 
          p.attaches_to === 'previous' && 
          !usedPunctuationIds.has(p.id) &&
          Math.abs(p.start_time - word.end_time) < 0.1 // Within 100ms of word end
        );

        if (attachedPunct) {
          newWordIds.push(attachedPunct.id);
          usedPunctuationIds.add(attachedPunct.id);
        }
      }

      skeleton.words = newWordIds;
    }

    // Keep used words, but preserve silence/punctuation tokens for future chunking
    const usedWordIds = new Set(newSkeletons.flatMap(s => s.words));
    const newWords = currentCaptionData.words.filter(w => (
      usedWordIds.has(w.id) ||
      w.type === 'silence' ||
      w.type === 'punctuation'
    ));

    updateCaptionData({ skeleton: newSkeletons, words: newWords });
  }, [updateCaptionData]);

  // Apply auto line breaks to words based on wordsPerLine setting
  // This sets break: true on words at the wordsPerLine position in each skeleton
  const applyAutoLineBreaks = useCallback((wordsPerLine) => {
    // First, clear all existing breaks
    let newWords = captionData.words.map(w => ({ ...w, break: false }));

    // For each skeleton, find the word at wordsPerLine position and set break: true
    captionData.skeleton.forEach((skeleton) => {
      // Get display words (excluding silences and attached punctuation)
      const displayWordIds = skeleton.words.filter(wordId => {
        const word = newWords.find(w => w.id === wordId);
        if (!word) return false;
        if (word.type === 'silence') return false;
        if (word.type === 'punctuation' && word.attaches_to === 'previous') return false;
        return true;
      });

      // Set break on the word at wordsPerLine - 1 position (0-indexed)
      // Only if there are more words after it
      if (wordsPerLine > 0 && displayWordIds.length > wordsPerLine) {
        const breakWordId = displayWordIds[wordsPerLine - 1];
        newWords = newWords.map(w =>
          w.id === breakWordId ? { ...w, break: true } : w
        );
      }
    });

    updateCaptionData({ ...captionData, words: newWords });
  }, [captionData, updateCaptionData]);

  // ============== PHRASE MANAGEMENT FUNCTIONS ==============

  // O(1) word-to-phrase lookup map — rebuilt only when phrases change
  const wordToPhraseMap = useMemo(() => {
    const map = {};
    if (!captionData.phrases) return map;
    for (const phrase of captionData.phrases) {
      if (phrase.currentWordIds) {
        for (const wid of phrase.currentWordIds) {
          map[wid] = phrase;
        }
      }
    }
    return map;
  }, [captionData.phrases]);

  // Get phrase that contains a specific word
  const getPhraseForWord = useCallback((wordId) => {
    return wordToPhraseMap[wordId] || null;
  }, [wordToPhraseMap]);

  // Find which skeleton(s) contain the phrase's current words
  const findSkeletonsForPhrase = useCallback((phrase) => {
    if (!phrase) return { skeletons: [], isSingleSkeleton: false };
    
    const skeletonInfos = [];
    const seenSkeletonIds = new Set();
    
    for (const wordId of phrase.currentWordIds) {
      for (let i = 0; i < captionData.skeleton.length; i++) {
        const skeleton = captionData.skeleton[i];
        if (skeleton.words.includes(wordId) && !seenSkeletonIds.has(skeleton.id)) {
          seenSkeletonIds.add(skeleton.id);
          const wordPosition = skeleton.words.indexOf(wordId);
          skeletonInfos.push({
            skeleton,
            skeletonIndex: i,
            wordPosition
          });
        }
      }
    }
    
    // Sort by skeleton index
    skeletonInfos.sort((a, b) => a.skeletonIndex - b.skeletonIndex);
    
    return {
      skeletons: skeletonInfos,
      isSingleSkeleton: skeletonInfos.length === 1
    };
  }, [captionData.skeleton]);

  // Merge multiple consecutive skeletons into one
  const mergeSkeletons = useCallback((skeletonIndexes) => {
    if (!skeletonIndexes || skeletonIndexes.length < 2) return;

    // Sort indexes to ensure we process in order
    const sortedIndexes = [...skeletonIndexes].sort((a, b) => a - b);

    // Check that all indexes are consecutive
    for (let i = 1; i < sortedIndexes.length; i++) {
      if (sortedIndexes[i] !== sortedIndexes[i - 1] + 1) {
        console.error('Can only merge consecutive skeletons');
        return;
      }
    }

    const firstIndex = sortedIndexes[0];
    const skeletonsToMerge = sortedIndexes.map(idx => captionData.skeleton[idx]);

    // Combine all word IDs
    const mergedWordIds = skeletonsToMerge.flatMap(s => s.words);

    // Calculate bounds from actual word timestamps
    const fallbackStart = skeletonsToMerge[0].start_time;
    const fallbackEnd = skeletonsToMerge[skeletonsToMerge.length - 1].end_time;
    const bounds = calculateSkeletonBounds(mergedWordIds, captionData.words, fallbackStart, fallbackEnd);

    // Create merged skeleton
    const mergedSkeleton = {
      ...skeletonsToMerge[0],
      words: mergedWordIds,
      start_time: bounds.start_time,
      end_time: bounds.end_time
    };

    // Build new skeleton array
    const newSkeletons = [
      ...captionData.skeleton.slice(0, firstIndex),
      mergedSkeleton,
      ...captionData.skeleton.slice(sortedIndexes[sortedIndexes.length - 1] + 1)
    ];

    updateCaptionData({
      ...captionData,
      skeleton: newSkeletons
    });

    return mergedSkeleton;
  }, [captionData, updateCaptionData]);

  // Merge skeletons AND apply phrase alternative in one atomic operation
  // This fixes the issue where merge and replace would only merge without replacing
  const mergeSkeletonsAndApplyAlternative = useCallback((skeletonIndexes, phraseId, alternativeId) => {
    if (!skeletonIndexes || skeletonIndexes.length < 2) {
      return { success: false, error: 'Need at least 2 skeletons to merge' };
    }

    // Sort indexes to ensure we process in order
    const sortedIndexes = [...skeletonIndexes].sort((a, b) => a - b);

    // Check that all indexes are consecutive
    for (let i = 1; i < sortedIndexes.length; i++) {
      if (sortedIndexes[i] !== sortedIndexes[i - 1] + 1) {
        return { success: false, error: 'Can only merge consecutive skeletons' };
      }
    }

    const firstIndex = sortedIndexes[0];
    const skeletonsToMerge = sortedIndexes.map(idx => captionData.skeleton[idx]);

    // Combine all word IDs
    const mergedWordIds = skeletonsToMerge.flatMap(s => s.words);

    // Calculate bounds from actual word timestamps
    const fallbackStart = skeletonsToMerge[0].start_time;
    const fallbackEnd = skeletonsToMerge[skeletonsToMerge.length - 1].end_time;
    const bounds = calculateSkeletonBounds(mergedWordIds, captionData.words, fallbackStart, fallbackEnd);

    // Create merged skeleton
    const mergedSkeleton = {
      ...skeletonsToMerge[0],
      words: mergedWordIds,
      start_time: bounds.start_time,
      end_time: bounds.end_time
    };

    // Build new skeleton array
    let newSkeletons = [
      ...captionData.skeleton.slice(0, firstIndex),
      mergedSkeleton,
      ...captionData.skeleton.slice(sortedIndexes[sortedIndexes.length - 1] + 1)
    ];

    // Now apply the phrase alternative in the same operation
    const phrases = captionData.phrases || [];
    const phrase = phrases.find(p => p.id === phraseId);
    
    if (!phrase) {
      // Just do the merge without alternative
      updateCaptionData({ ...captionData, skeleton: newSkeletons });
      return { success: true, merged: true, alternativeApplied: false };
    }

    // Get the alternative (null means restore original)
    const alternative = alternativeId !== null 
      ? phrase.alternatives.find(a => a.id === alternativeId)
      : null;
    
    if (alternativeId !== null && !alternative) {
      // Alternative not found, just do the merge
      updateCaptionData({ ...captionData, skeleton: newSkeletons });
      return { success: true, merged: true, alternativeApplied: false };
    }

    // Find the merged skeleton (it's at firstIndex now)
    const skeleton = newSkeletons[firstIndex];
    
    // Find insert position in the merged skeleton
    const firstPhraseWordIndex = skeleton.words.findIndex(wid => phrase.currentWordIds.includes(wid));
    const insertPosition = firstPhraseWordIndex !== -1 ? firstPhraseWordIndex : 0;
    
    // Remove current phrase words from words array and skeleton
    const currentWordIdSet = new Set(phrase.currentWordIds);
    let newWords = captionData.words.filter(w => !currentWordIdSet.has(w.id));
    let newSkeletonWords = skeleton.words.filter(id => !currentWordIdSet.has(id));
    
    let newWordIds;
    let wordsToAdd;
    
    if (alternativeId === null) {
      // Restore original words
      newWordIds = [...phrase.originalWordIds];
      wordsToAdd = phrase.originalWords.map(w => ({ ...w }));
    } else {
      // Apply alternative
      const phraseStart = Math.min(...phrase.originalWords.map(w => w.start_time));
      const phraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
      const phraseDuration = phraseEnd - phraseStart;
      
      newWordIds = [];
      wordsToAdd = alternative.words.map((altWord, index) => {
        const wordId = `${phraseId}_w${index}`;
        newWordIds.push(wordId);
        
        let startTime, endTime;
        if (alternative.timingMode === 'absolute' && 
            altWord.absoluteStart != null && 
            altWord.absoluteEnd != null) {
          startTime = altWord.absoluteStart;
          endTime = altWord.absoluteEnd;
        } else {
          startTime = phraseStart + (phraseDuration * altWord.relativeStart);
          endTime = phraseStart + (phraseDuration * altWord.relativeEnd);
        }
        
        return {
          id: wordId,
          text: altWord.text,
          type: 'word',
          start_time: startTime,
          end_time: endTime,
          highlightColor: altWord.highlightColor || null,
          break: false,
          phraseId: phraseId
        };
      });
    }
    
    // Add new words to words array
    newWords = [...newWords, ...wordsToAdd];
    
    // Insert new word IDs into skeleton at the correct position
    newSkeletonWords.splice(insertPosition, 0, ...newWordIds);
    
    // Recalculate skeleton timing
    const skeletonWordsData = newSkeletonWords
      .map(wid => newWords.find(w => w.id === wid))
      .filter(w => w && w.type !== 'silence');
    
    let updatedStartTime = skeleton.start_time;
    let updatedEndTime = skeleton.end_time;
    
    if (skeletonWordsData.length > 0) {
      const wordStartTimes = skeletonWordsData
        .map(w => w.start_time)
        .filter(t => t !== undefined && t !== null);
      const wordEndTimes = skeletonWordsData
        .map(w => w.end_time)
        .filter(t => t !== undefined && t !== null);
      
      if (wordStartTimes.length > 0 && wordEndTimes.length > 0) {
        updatedStartTime = Math.min(...wordStartTimes);
        updatedEndTime = Math.max(...wordEndTimes);
      }
    }
    
    newSkeletons[firstIndex] = {
      ...newSkeletons[firstIndex],
      words: newSkeletonWords,
      start_time: updatedStartTime,
      end_time: updatedEndTime
    };
    
    // Update phrase
    const newPhrases = phrases.map(p =>
      p.id === phraseId
        ? { ...p, activeAlternativeId: alternativeId, currentWordIds: newWordIds }
        : p
    );
    
    updateCaptionData({
      skeleton: newSkeletons,
      words: newWords,
      phrases: newPhrases
    });
    
    return { success: true, merged: true, alternativeApplied: true };
  }, [captionData, updateCaptionData]);

  // Undo function
  const undo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const previousState = historyRef.current[newIndex];
      
      if (previousState) {
        historyIndexRef.current = newIndex;
        setCaptionData(previousState);
        forceUpdate({}); // Trigger re-render to update canUndo/canRedo
        showToast('↩ Undo', 'undo');
      }
    } else {
      showToast('Nothing to undo', 'warning');
    }
  }, [showToast]);

  // Redo function
  const redo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    const historyLength = historyRef.current.length;
    
    if (currentIndex < historyLength - 1) {
      const newIndex = currentIndex + 1;
      const nextState = historyRef.current[newIndex];
      
      if (nextState) {
        historyIndexRef.current = newIndex;
        setCaptionData(nextState);
        forceUpdate({}); // Trigger re-render to update canUndo/canRedo
        showToast('↪ Redo', 'redo');
      }
    } else {
      showToast('Nothing to redo', 'warning');
    }
  }, [showToast]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          // Cmd+Shift+Z or Ctrl+Shift+Z for redo
          redo();
        } else {
          // Cmd+Z or Ctrl+Z for undo
          undo();
        }
      }
      // Alternative redo shortcut: Cmd+Y or Ctrl+Y
      else if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [undo, redo]);


  // Select/activate a phrase alternative (or revert to original with null)
  const selectPhraseAlternative = useCallback((phraseId, alternativeId) => {
    const phrases = captionData.phrases || [];
    const phrase = phrases.find(p => p.id === phraseId);
    if (!phrase) return { success: false, error: 'Phrase not found' };
    
    // Find which skeleton(s) contain the phrase
    const { skeletons, isSingleSkeleton } = findSkeletonsForPhrase(phrase);
    
    if (skeletons.length === 0) {
      return { success: false, error: 'Phrase words not found in any skeleton' };
    }
    
    // If multiple skeletons and trying to activate (not revert), require merge first
    if (!isSingleSkeleton && alternativeId !== null) {
      return { 
        success: false, 
        needsMerge: true, 
        skeletonIndexes: skeletons.map(s => s.skeletonIndex),
        message: 'This phrase spans multiple lines. Merge them first to use alternatives.'
      };
    }
    
    // Get the skeleton info (should be single now, or we're reverting)
    const { skeleton, skeletonIndex, wordPosition } = skeletons[0];
    
    let newWords = [...captionData.words];
    let newSkeletonWords = [...skeleton.words];
    
    // Remove current phrase words from words array and skeleton
    const currentWordIdSet = new Set(phrase.currentWordIds);
    // Check if any of the phrase words being replaced are protected (trial blur)
    const phraseIsProtected = newWords.some(w => currentWordIdSet.has(w.id) && w.is_protected);
    newWords = newWords.filter(w => !currentWordIdSet.has(w.id));
    
    // Find positions of phrase words in skeleton and remove them
    const phrasePositions = phrase.currentWordIds
      .map(id => newSkeletonWords.indexOf(id))
      .filter(pos => pos !== -1)
      .sort((a, b) => a - b);
    
    const insertPosition = phrasePositions.length > 0 ? phrasePositions[0] : wordPosition;
    newSkeletonWords = newSkeletonWords.filter(id => !currentWordIdSet.has(id));
    
    let newWordIds;
    let wordsToAdd;
    
    if (alternativeId === null) {
      // Restore original words
      newWordIds = [...phrase.originalWordIds];
      wordsToAdd = phrase.originalWords.map(w => {
        const word = { ...w };
        // Carry over protected flag so blurred words stay blurred
        if (phraseIsProtected) {
          word.is_protected = true;
        }
        return word;
      });
    } else {
      // Activate alternative
      const alternative = phrase.alternatives.find(a => a.id === alternativeId);
      if (!alternative) {
        return { success: false, error: 'Alternative not found' };
      }
      
      // Calculate timing from original words
      const phraseStart = Math.min(...phrase.originalWords.map(w => w.start_time));
      const phraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
      const phraseDuration = phraseEnd - phraseStart;
      
      // Generate new words with computed timing
      newWordIds = [];
      wordsToAdd = alternative.words.map((altWord, index) => {
        const wordId = `${phraseId}_w${index}`;
        newWordIds.push(wordId);
        
        let startTime, endTime;
        if (alternative.timingMode === 'absolute' && 
            altWord.absoluteStart != null && 
            altWord.absoluteEnd != null) {
          startTime = altWord.absoluteStart;
          endTime = altWord.absoluteEnd;
        } else {
          startTime = phraseStart + (phraseDuration * altWord.relativeStart);
          endTime = phraseStart + (phraseDuration * altWord.relativeEnd);
        }
        
        const word = {
          id: wordId,
          text: altWord.text,
          type: 'word',
          start_time: startTime,
          end_time: endTime,
          highlightColor: altWord.highlightColor || null,
          break: false,
          phraseId: phraseId
        };
        // Carry over protected flag so blurred words stay blurred
        if (phraseIsProtected) {
          word.is_protected = true;
        }
        return word;
      });
    }
    
    // Add new words to words array
    newWords = [...newWords, ...wordsToAdd];
    
    // Insert new word IDs into skeleton at the correct position
    newSkeletonWords.splice(insertPosition, 0, ...newWordIds);
    
    // Recalculate skeleton start/end times based on the words in it
    const skeletonWordsData = newSkeletonWords
      .map(wid => newWords.find(w => w.id === wid))
      .filter(w => w && w.type !== 'silence'); // Exclude silence and invalid words
    
    let updatedStartTime = skeleton.start_time;
    let updatedEndTime = skeleton.end_time;
    
    if (skeletonWordsData.length > 0) {
      // Calculate new skeleton boundaries from actual word times
      const wordStartTimes = skeletonWordsData
        .map(w => w.start_time)
        .filter(t => t !== undefined && t !== null);
      const wordEndTimes = skeletonWordsData
        .map(w => w.end_time)
        .filter(t => t !== undefined && t !== null);
      
      if (wordStartTimes.length > 0 && wordEndTimes.length > 0) {
        updatedStartTime = Math.min(...wordStartTimes);
        updatedEndTime = Math.max(...wordEndTimes);
      }
    }
    
    // Update skeleton with new words and adjusted timing
    const newSkeleton = captionData.skeleton.map((s, i) => 
      i === skeletonIndex 
        ? { 
            ...s, 
            words: newSkeletonWords,
            start_time: updatedStartTime,
            end_time: updatedEndTime
          }
        : s
    );
    
    // Update phrase
    const newPhrases = phrases.map(p =>
      p.id === phraseId
        ? { ...p, activeAlternativeId: alternativeId, currentWordIds: newWordIds }
        : p
    );
    
    // Convert attached punctuation when switching scripts
    // Determine if we're switching to Latin (code_switched) or Arabic (original/other)
    const isToLatin = alternativeId === 'alt_code_switched';
    const wasLatin = phrase.activeAlternativeId === 'alt_code_switched';
    
    if (isToLatin !== wasLatin) {
      // Script changed - find and convert attached punctuation
      // Use the ORIGINAL phrase's end time to find punctuation (since that's when it was timed)
      const originalPhraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
      
      // Find punctuation that attaches to this phrase (by timing proximity to original end)
      const punctuationToConvert = newWords.filter(w => 
        w.type === 'punctuation' &&
        w.attaches_to === 'previous' &&
        Math.abs(w.start_time - originalPhraseEnd) < 0.3
      );
      
      for (const punct of punctuationToConvert) {
        const targetScript = isToLatin ? 'latin' : 'arabic';
        const convertedText = convertPunctuation(punct.text, targetScript);
        if (convertedText !== punct.text) {
          // Update the punctuation word in newWords
          const punctIndex = newWords.findIndex(w => w.id === punct.id);
          if (punctIndex !== -1) {
            newWords[punctIndex] = { ...newWords[punctIndex], text: convertedText };
          }
        }
      }
    }
    
    updateCaptionData({
      skeleton: newSkeleton,
      words: newWords,
      phrases: newPhrases
    });
    
    // Trigger animation feedback for the new words
    markWordsAsChanged(newWordIds);
    
    return { success: true };
  }, [captionData, updateCaptionData, findSkeletonsForPhrase, markWordsAsChanged]);

  // Apply phrase display mode to all phrases at once
  // This activates the specified alternative type for all phrases
  // 'default' mode now applies transliterated (Arabic script) version
  // forceTransform: if true, force transformation even if activeAlternativeId matches (for initial load)
  const applyPhraseDisplayMode = useCallback((mode, forceTransform = false, sourceCaptionData = null) => {
    // Use provided snapshot or ref to get latest captionData (avoids stale closure issues)
    const currentCaptionData = sourceCaptionData || captionDataRef.current;
    
    
    setPhraseDisplayMode(mode);
    
    if (!currentCaptionData.phrases || currentCaptionData.phrases.length === 0) {
      return;
    }
    
    // Map 'default' to 'transliterated' for actual application
    const effectiveMode = mode === 'default' ? 'transliterated' : mode;
    
    const phrases = currentCaptionData.phrases || [];
    let newWords = [...currentCaptionData.words];
    let newSkeletons = [...currentCaptionData.skeleton];
    let newPhrases = [...phrases];

    const getSkeletonIndexesForPhrase = (phrase, skeletons) => {
      if (!phrase) return [];
      const wordIdSet = new Set(phrase.currentWordIds);
      const indexes = [];
      for (let i = 0; i < skeletons.length; i++) {
        const skel = skeletons[i];
        if (skel.words.some(id => wordIdSet.has(id))) {
          indexes.push(i);
        }
      }
      return indexes;
    };
    
    // Process each phrase
    for (const phrase of phrases) {
      
      // Determine which alternative to activate based on effectiveMode
      let targetAltId = null;
      
      if (effectiveMode === 'code_switched') {
        targetAltId = 'alt_code_switched';
      } else if (effectiveMode === 'transliterated') {
        targetAltId = 'alt_transliterated';
      } else if (effectiveMode === 'translated') {
        // Find first translated alternative
        const translatedAlt = phrase.alternatives.find(a => a.id.startsWith('alt_translated'));
        targetAltId = translatedAlt?.id || null;
      }
      
      // Determine if we should skip this phrase
      // - If forceTransform is true (initial load): only skip if no targetAltId
      // - If forceTransform is false (settings panel): skip if already at target
      const shouldSkip = !targetAltId || (!forceTransform && phrase.activeAlternativeId === targetAltId);
      
      
      if (shouldSkip) {
        continue;
      }
      
      const alternative = phrase.alternatives.find(a => a.id === targetAltId);
      if (!alternative) continue;

      // If phrase spans multiple chunks, merge them first so the transformation applies to all words
      const skeletonIndexes = getSkeletonIndexesForPhrase(phrase, newSkeletons);
      if (skeletonIndexes.length > 1) {
        const firstIndex = Math.min(...skeletonIndexes);
        const lastIndex = Math.max(...skeletonIndexes);
        const skeletonsToMerge = newSkeletons.slice(firstIndex, lastIndex + 1);
        const mergedWordIds = skeletonsToMerge.flatMap(s => s.words);
        const fallbackStart = skeletonsToMerge[0].start_time;
        const fallbackEnd = skeletonsToMerge[skeletonsToMerge.length - 1].end_time;
        const bounds = calculateSkeletonBounds(mergedWordIds, newWords, fallbackStart, fallbackEnd);
        const mergedSkeleton = {
          ...skeletonsToMerge[0],
          words: mergedWordIds,
          start_time: bounds.start_time,
          end_time: bounds.end_time
        };

        newSkeletons = [
          ...newSkeletons.slice(0, firstIndex),
          mergedSkeleton,
          ...newSkeletons.slice(lastIndex + 1)
        ];
      }
      
      // Find skeleton containing this phrase's words
      let skeletonIndex = -1;
      let insertPosition = 0;
      let phraseWordIdsForOps = phrase.currentWordIds || [];
      
      const findSkeletonPosition = (wordIds) => {
        if (!wordIds || wordIds.length === 0) return { index: -1, position: 0 };
        for (let i = 0; i < newSkeletons.length; i++) {
          const skeleton = newSkeletons[i];
          const firstPhraseWord = wordIds[0];
          const position = skeleton.words.indexOf(firstPhraseWord);
          if (position !== -1) {
            return { index: i, position };
          }
        }
        return { index: -1, position: 0 };
      };

      const primaryPos = findSkeletonPosition(phraseWordIdsForOps);
      skeletonIndex = primaryPos.index;
      insertPosition = primaryPos.position;
      
      // Fallback: if currentWordIds don't exist in skeletons, use originalWords ids
      if (skeletonIndex === -1 && phrase.originalWords?.length) {
        const fallbackIds = phrase.originalWords.map(w => w.id);
        const fallbackPos = findSkeletonPosition(fallbackIds);
        skeletonIndex = fallbackPos.index;
        insertPosition = fallbackPos.position;
        if (skeletonIndex !== -1) {
          phraseWordIdsForOps = fallbackIds;
        }
      }
      
      
      if (skeletonIndex === -1) continue;
      
      // Remove current phrase words from words array and skeleton
      const currentWordIdSet = new Set(phraseWordIdsForOps);
      // Check if any of the phrase words being replaced are protected (trial blur)
      const phraseIsProtected = newWords.some(w => currentWordIdSet.has(w.id) && w.is_protected);
      newWords = newWords.filter(w => !currentWordIdSet.has(w.id));
      
      let skeletonWords = [...newSkeletons[skeletonIndex].words];
      skeletonWords = skeletonWords.filter(id => !currentWordIdSet.has(id));
      
      // Calculate timing from original words
      const phraseStart = Math.min(...phrase.originalWords.map(w => w.start_time));
      const phraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
      const phraseDuration = phraseEnd - phraseStart;
      
      // Generate new words with computed timing
      const newWordIds = [];
      const wordsToAdd = alternative.words.map((altWord, index) => {
        const wordId = `${phrase.id}_w${index}`;
        newWordIds.push(wordId);
        
        let startTime, endTime;
        if (alternative.timingMode === 'absolute' && 
            altWord.absoluteStart != null && 
            altWord.absoluteEnd != null) {
          startTime = altWord.absoluteStart;
          endTime = altWord.absoluteEnd;
        } else {
          startTime = phraseStart + (phraseDuration * altWord.relativeStart);
          endTime = phraseStart + (phraseDuration * altWord.relativeEnd);
        }
        
        const word = {
          id: wordId,
          text: altWord.text,
          type: 'word',
          start_time: startTime,
          end_time: endTime,
          highlightColor: altWord.highlightColor || null,
          break: false,
          phraseId: phrase.id
        };
        // Carry over the protected flag so blurred words stay blurred after phrase switch
        if (phraseIsProtected) {
          word.is_protected = true;
        }
        return word;
      });
      
      
      // Add new words
      newWords = [...newWords, ...wordsToAdd];
      
      // Insert new word IDs into skeleton
      skeletonWords.splice(insertPosition, 0, ...newWordIds);
      
      
      // Recalculate skeleton start/end times based on the words in it
      const skeletonWordsData = skeletonWords
        .map(wid => newWords.find(w => w.id === wid))
        .filter(w => w && w.type !== 'silence');
      
      let updatedStartTime = newSkeletons[skeletonIndex].start_time;
      let updatedEndTime = newSkeletons[skeletonIndex].end_time;
      
      if (skeletonWordsData.length > 0) {
        const wordStartTimes = skeletonWordsData
          .map(w => w.start_time)
          .filter(t => t !== undefined && t !== null);
        const wordEndTimes = skeletonWordsData
          .map(w => w.end_time)
          .filter(t => t !== undefined && t !== null);
        
        if (wordStartTimes.length > 0 && wordEndTimes.length > 0) {
          updatedStartTime = Math.min(...wordStartTimes);
          updatedEndTime = Math.max(...wordEndTimes);
        }
      }
      
      newSkeletons[skeletonIndex] = { 
        ...newSkeletons[skeletonIndex], 
        words: skeletonWords,
        start_time: updatedStartTime,
        end_time: updatedEndTime
      };
      
      // Update phrase in newPhrases
      const phraseIndex = newPhrases.findIndex(p => p.id === phrase.id);
      if (phraseIndex !== -1) {
        newPhrases[phraseIndex] = {
          ...newPhrases[phraseIndex],
          activeAlternativeId: targetAltId,
          currentWordIds: newWordIds
        };
      }
      
      // Convert attached punctuation when switching scripts
      const isToLatin = targetAltId === 'alt_code_switched';
      const wasLatin = phrase.activeAlternativeId === 'alt_code_switched';
      
      if (isToLatin !== wasLatin) {
        // Script changed - find and convert attached punctuation
        // Use the ORIGINAL phrase's end time to find punctuation
        const originalPhraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
        
        // Find punctuation that attaches to this phrase (by timing proximity to original end)
        for (let pi = 0; pi < newWords.length; pi++) {
          const punct = newWords[pi];
          if (punct.type === 'punctuation' &&
              punct.attaches_to === 'previous' &&
              Math.abs(punct.start_time - originalPhraseEnd) < 0.3) {
            const targetScript = isToLatin ? 'latin' : 'arabic';
            const convertedText = convertPunctuation(punct.text, targetScript);
            if (convertedText !== punct.text) {
              newWords[pi] = { ...punct, text: convertedText };
            }
          }
        }
      }
    }
    
    
    updateCaptionData({
      skeleton: newSkeletons,
      words: newWords,
      phrases: newPhrases
    });
    
    const modeLabel = mode === 'default' ? 'transliterated (Arabic script)' : effectiveMode.replace('_', ' ');
    showToast(translate('Phrases set to {mode}', { mode: translate(modeLabel) }), 'success');
  }, [updateCaptionData, showToast]); // Note: using captionDataRef instead of captionData to avoid stale closure

  // ============== TRANSLATION FUNCTIONS ==============
  
  // Request translation from server
  const requestTranslation = useCallback(async (jobId, language = 'en') => {
    if (!jobId) return { success: false, error: 'No job ID' };
    if (translationRequested) return { success: false, error: 'Translation already requested' };
    
    try {
      setTranslationStatus('pending');
      const response = await editorService.requestTranslation(jobId, language);
      
      if (response) {
        setTranslationRequested(true);
        showToast('Translation requested! This may take a few seconds...', 'success');
        
        // If already completed (from cache), set the translation
        if (response.status === 'completed' && response.translation) {
          setServerTranslation(response.translation);
          const editableTranslation = initializeTranslationFromServer(response.translation);
          setTranslationData(editableTranslation);
          setTranslationStatus('completed');
        }
        
        return { success: true, status: response.status };
      }
      return { success: false, error: 'No response from server' };
    } catch (error) {
      setTranslationStatus('error');
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to request translation';
      showToast(errorMessage, 'warning');
      return { success: false, error: errorMessage };
    }
  }, [translationRequested, showToast]);
  
  // Update translation data (for editing)
  const updateTranslationData = useCallback((newTranslationData, skipHistory = false) => {
    setTranslationData(prev => {
      if (!prev) return newTranslationData;
      return {
        ...prev,
        ...newTranslationData
      };
    });
  }, []);
  
  // Update a specific translation word
  const updateTranslationWord = useCallback((wordId, updates) => {
    if (!translationData) return;
    
    const newWords = translationData.words.map(word =>
      word.id === wordId ? { ...word, ...updates } : word
    );
    
    setTranslationData(prev => ({
      ...prev,
      words: newWords
    }));
    markWordsAsChanged(wordId);
  }, [translationData, markWordsAsChanged]);
  
  // Update translation skeleton
  const updateTranslationSkeleton = useCallback((skeletonIndex, updates) => {
    if (!translationData) return;
    
    const newSkeleton = translationData.skeleton.map((skel, idx) =>
      idx === skeletonIndex ? { ...skel, ...updates } : skel
    );
    
    setTranslationData(prev => ({
      ...prev,
      skeleton: newSkeleton
    }));
  }, [translationData]);
  
  // Toggle translation view (original vs translated)
  const toggleTranslationView = useCallback(() => {
    if (!translationData && !serverTranslation) {
      showToast('No translation available. Request a translation first.', 'warning');
      return;
    }
    setIsTranslationView(prev => !prev);
  }, [translationData, serverTranslation, showToast]);

  
  // Get current data based on view mode (original or translated)
  const getCurrentViewData = useCallback(() => {
    if (isTranslationView && translationData) {
      return {
        skeleton: translationData.skeleton || [],
        words: translationData.words || [],
        phrases: [] // Translations don't have phrases
      };
    }
    return captionData;
  }, [isTranslationView, translationData, captionData]);
  
  // Computed view data - use this in components to respect translation view
  const viewCaptionData = useMemo(() => {
    if (isTranslationView && translationData) {
      return {
        skeleton: translationData.skeleton || [],
        words: translationData.words || [],
        phrases: [] // Translations don't have phrases
      };
    }
    return captionData;
  }, [isTranslationView, translationData, captionData]);
  
  // Update word - works for both original and translation view
  const updateViewWord = useCallback((wordId, updates) => {
    if (isTranslationView && translationData) {
      // Update translation word
      const newWords = translationData.words.map(word =>
        word.id === wordId ? { ...word, ...updates } : word
      );
      setTranslationData(prev => ({
        ...prev,
        words: newWords
      }));
    } else {
      // Update original word
      updateWord(wordId, updates);
    }
    markWordsAsChanged(wordId);
  }, [isTranslationView, translationData, updateWord, markWordsAsChanged]);
  
  // Refresh translation from server (for polling)
  const refreshTranslation = useCallback(async (jobId) => {
    if (!jobId) return;
    
    try {
      const response = await editorService.getEditorData(jobId);
      if (response?.translation) {
        setServerTranslation(response.translation);
        
        // Only update editable translation if user hasn't made changes
        if (!translationData) {
          const editableTranslation = initializeTranslationFromServer(response.translation);
          setTranslationData(editableTranslation);
        }
        
        setTranslationStatus('completed');
        showToast('Translation ready!', 'success');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to refresh translation:', error);
      return false;
    }
  }, [translationData, showToast]);
  
  // Custom setDisplayMode that automatically handles phrase mode when switching to/from arabizi
  const setDisplayMode = useCallback((newMode) => {
    const currentMode = displayMode;
    setDisplayModeInternal(newMode);
    
    // When switching TO arabizi, automatically apply code_switched to phrases
    if (newMode === 'arabizi' && currentMode !== 'arabizi') {
      // Store current phrase mode before switching
      setPreviousPhraseMode(phraseDisplayMode);
      // Apply code_switched (Latin) to all phrases - do this after a tick to ensure state is updated
      setTimeout(() => {
        // Only apply if there are phrases
        if (captionData.phrases && captionData.phrases.length > 0) {
          // Silently apply code_switched without toast
          setPhraseDisplayMode('code_switched');
          // Apply the actual phrase changes
          const phrases = captionData.phrases || [];
          let newWords = [...captionData.words];
          let newSkeletons = [...captionData.skeleton];
          let newPhrases = [...phrases];
          
          for (const phrase of phrases) {
            const targetAltId = 'alt_code_switched';
            if (phrase.activeAlternativeId === targetAltId) continue;
            
            const alternative = phrase.alternatives.find(a => a.id === targetAltId);
            if (!alternative) continue;
            
            let skeletonIndex = -1;
            let insertPosition = 0;
            
            for (let i = 0; i < newSkeletons.length; i++) {
              const skeleton = newSkeletons[i];
              const firstPhraseWord = phrase.currentWordIds[0];
              const position = skeleton.words.indexOf(firstPhraseWord);
              if (position !== -1) {
                skeletonIndex = i;
                insertPosition = position;
                break;
              }
            }
            
            if (skeletonIndex === -1) continue;
            
            const currentWordIdSet = new Set(phrase.currentWordIds);
            newWords = newWords.filter(w => !currentWordIdSet.has(w.id));
            
            let skeletonWords = [...newSkeletons[skeletonIndex].words];
            skeletonWords = skeletonWords.filter(id => !currentWordIdSet.has(id));
            
            const phraseStart = Math.min(...phrase.originalWords.map(w => w.start_time));
            const phraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
            const phraseDuration = phraseEnd - phraseStart;
            
            const newWordIds = [];
            const wordsToAdd = alternative.words.map((altWord, index) => {
              const wordId = `${phrase.id}_w${index}`;
              newWordIds.push(wordId);
              
              let startTime, endTime;
              if (alternative.timingMode === 'absolute' && altWord.absoluteStart != null && altWord.absoluteEnd != null) {
                startTime = altWord.absoluteStart;
                endTime = altWord.absoluteEnd;
              } else {
                startTime = phraseStart + (phraseDuration * altWord.relativeStart);
                endTime = phraseStart + (phraseDuration * altWord.relativeEnd);
              }
              
              return {
                id: wordId,
                text: altWord.text,
                type: 'word',
                start_time: startTime,
                end_time: endTime,
                highlightColor: altWord.highlightColor || null,
                break: false,
                phraseId: phrase.id
              };
            });
            
            newWords = [...newWords, ...wordsToAdd];
            skeletonWords.splice(insertPosition, 0, ...newWordIds);
            
            // Recalculate skeleton start/end times based on the words in it
            const skeletonWordsData = skeletonWords
              .map(wid => newWords.find(w => w.id === wid))
              .filter(w => w && w.type !== 'silence');
            
            let updatedStartTime = newSkeletons[skeletonIndex].start_time;
            let updatedEndTime = newSkeletons[skeletonIndex].end_time;
            
            if (skeletonWordsData.length > 0) {
              const wordStartTimes = skeletonWordsData
                .map(w => w.start_time)
                .filter(t => t !== undefined && t !== null);
              const wordEndTimes = skeletonWordsData
                .map(w => w.end_time)
                .filter(t => t !== undefined && t !== null);
              
              if (wordStartTimes.length > 0 && wordEndTimes.length > 0) {
                updatedStartTime = Math.min(...wordStartTimes);
                updatedEndTime = Math.max(...wordEndTimes);
              }
            }
            
            newSkeletons[skeletonIndex] = { 
              ...newSkeletons[skeletonIndex], 
              words: skeletonWords,
              start_time: updatedStartTime,
              end_time: updatedEndTime
            };
            
            const phraseIndex = newPhrases.findIndex(p => p.id === phrase.id);
            if (phraseIndex !== -1) {
              newPhrases[phraseIndex] = {
                ...newPhrases[phraseIndex],
                activeAlternativeId: targetAltId,
                currentWordIds: newWordIds
              };
            }
            
            // Convert attached punctuation when switching to Latin
            const wasLatin = phrase.activeAlternativeId === 'alt_code_switched';
            if (!wasLatin) {
              // Switching to Latin - find and convert attached punctuation
              // Use the ORIGINAL phrase's end time to find punctuation
              const originalPhraseEnd = Math.max(...phrase.originalWords.map(w => w.end_time));
              
              for (let pi = 0; pi < newWords.length; pi++) {
                const punct = newWords[pi];
                if (punct.type === 'punctuation' &&
                    punct.attaches_to === 'previous' &&
                    Math.abs(punct.start_time - originalPhraseEnd) < 0.3) {
                  const convertedText = convertPunctuation(punct.text, 'latin');
                  if (convertedText !== punct.text) {
                    newWords[pi] = { ...punct, text: convertedText };
                  }
                }
              }
            }
          }
          
          updateCaptionData({
            skeleton: newSkeletons,
            words: newWords,
            phrases: newPhrases
          });
        }
      }, 0);
    }
    // When switching FROM arabizi back to original, restore previous phrase mode
    else if (newMode === 'original' && currentMode === 'arabizi') {
      // Restore previous phrase mode if it wasn't code_switched
      if (previousPhraseMode && previousPhraseMode !== 'code_switched') {
        setTimeout(() => {
          applyPhraseDisplayMode(previousPhraseMode);
        }, 0);
      }
    }
  }, [displayMode, phraseDisplayMode, previousPhraseMode, captionData, updateCaptionData, applyPhraseDisplayMode]);

  const value = {
    captionData,
    editorReady,
    setEditorReady,
    currentTime,
    setCurrentTime,
    updateCaptionData,
    videoSrc,
    setVideoSrc,
    activeJobId,
    setActiveJobId,
    loadFromServer,
    saveToServer,
    themeSettings,
    setThemeSettings,
    seekToTime,
    registerSeekCallback,
    // Display mode (original or arabizi)
    displayMode,
    setDisplayMode,
    getDisplayText,
    // Phrase display mode (default, code_switched, transliterated, translated)
    phraseDisplayMode,
    applyPhraseDisplayMode,
    // Auto-scroll
    autoScrollEnabled,
    setAutoScrollEnabled,
    // Undo/Redo
    undo,
    redo,
    canUndo: historyIndexRef.current > 0,
    canRedo: historyIndexRef.current < historyRef.current.length - 1,
    // Editor helpers
    getWordById,
    updateWord,
    updateSkeleton,
    updateSkeletonTiming,
    deleteSkeleton,
    splitSkeleton,
    mergeWithPrevious,
    addWord,
    splitWord,
    removeWord,
    mergeWords,
    mergeMultipleWords,
    mergeAndSplitWords,
    resetSkeleton,
    resetAllSkeletons,
    addSkeleton,
    reorganizeSkeletons,
    applyAutoLineBreaks,
    // Phrase helpers
    getPhraseForWord,
    findSkeletonsForPhrase,
    mergeSkeletons,
    mergeSkeletonsAndApplyAlternative,
    selectPhraseAlternative,
    // Toast
    toast,
    showToast,
    // Word change animation feedback
    changedWordIds,
    markWordsAsChanged,
    // New word tracking (different color until edited)
    newWordIds,
    markWordsAsNew,
    clearNewWordStatus,
    // Video ref
    videoRef,
    registerVideoRef,
    // Translation
    translationData,
    serverTranslation,
    translationRequested,
    isTranslationView,
    translationStatus,
    requestTranslation,
    updateTranslationData,
    updateTranslationWord,
    updateTranslationSkeleton,
    toggleTranslationView,
    setIsTranslationView,
    getCurrentViewData,
    refreshTranslation,
    // View-aware data (respects isTranslationView)
    viewCaptionData,
    updateViewWord,
    getViewWordById
  };

  return (
    <CaptionContext.Provider value={value}>
      {children}
      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </CaptionContext.Provider>
  );
};
